/**
 * WebSocket Service for MDM Real-time Updates
 *
 * Purpose: Manages WebSocket connections for real-time MDM data synchronization
 * Architecture: Event-driven WebSocket client with automatic reconnection and message routing
 * Dependencies: WebSocket API, MDM types, authentication service
 * Author: Development Team
 * Created: 2025-01-27
 */

import { WebSocketMessage, WebSocketMessageType } from '../types/mdm';

/**
 * WebSocket connection states
 */
export enum WebSocketState {
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED',
  RECONNECTING = 'RECONNECTING',
  ERROR = 'ERROR',
}

/**
 * WebSocket event callback types
 */
export interface WebSocketCallbacks {
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: string) => void;
  onMessage?: (message: WebSocketMessage) => void;
  onValueCreated?: (data: any) => void;
  onValueUpdated?: (data: any) => void;
  onValueDeleted?: (data: any) => void;
  onImportProgress?: (data: any) => void;
  onContextChanged?: (data: any) => void;
}

/**
 * WebSocket service configuration
 */
export interface WebSocketConfig {
  baseUrl?: string;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  pingInterval?: number;
  enableLogging?: boolean;
}

/**
 * MDM WebSocket Service Class
 *
 * Purpose: Provides real-time communication with MDM backend via WebSocket
 * Architecture: Event-driven service with automatic reconnection and message routing
 */
class WebSocketService {
  private ws: WebSocket | null = null;
  private config: WebSocketConfig;
  private callbacks: WebSocketCallbacks = {};
  private state: WebSocketState = WebSocketState.DISCONNECTED;
  private reconnectAttempts = 0;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private pingTimer: NodeJS.Timeout | null = null;
  private currentTenant: string | null = null;
  private token: string | null = null;
  private lastMessage: WebSocketMessage | null = null;

  /**
   * Initialize WebSocket Service
   *
   * @param config - Optional service configuration
   */
  constructor(config: WebSocketConfig = {}) {
    this.config = {
      baseUrl: this.getWebSocketUrl(),
      reconnectInterval: 5000,
      maxReconnectAttempts: 10,
      pingInterval: 30000,
      enableLogging: process.env.NODE_ENV === 'development',
      ...config,
    };
  }

  /**
   * Get WebSocket URL based on current environment
   */
  private getWebSocketUrl(): string {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;

    // In development, use backend port
    if (process.env.NODE_ENV === 'development') {
      return `${protocol}//${host.replace(':3000', ':3001')}/core-api/v1`;
    }

    return `${protocol}//${host}/core-api/v1`;
  }

  /**
   * Log messages in development mode
   */
  private log(message: string, data?: any): void {
    if (this.config.enableLogging) {
      console.log(`[WebSocketService] ${message}`, data || '');
    }
  }

  /**
   * Set event callbacks
   *
   * @param callbacks - Object containing callback functions
   */
  setCallbacks(callbacks: WebSocketCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Connect to WebSocket server
   *
   * @param tenant_id - Tenant context for the connection
   * @param token - JWT authentication token
   */
  connect(tenant_id: string, token: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.log('Already connected, disconnecting first');
      this.disconnect();
    }

    this.currentTenant = tenant_id;
    this.token = token;
    this.state = WebSocketState.CONNECTING;

    const url = `${this.config.baseUrl}/ws/${tenant_id}?token=${encodeURIComponent(token)}`;
    this.log('Connecting to WebSocket', { url: url.replace(token, '[TOKEN]'), tenant_id });

    try {
      this.ws = new WebSocket(url);
      this.setupEventHandlers();
    } catch (error) {
      this.log('Failed to create WebSocket connection', error);
      this.handleError('Failed to create WebSocket connection');
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.log('Disconnecting WebSocket');

    this.state = WebSocketState.DISCONNECTED;
    this.reconnectAttempts = 0;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.pingTimer) {
      clearTimeout(this.pingTimer);
      this.pingTimer = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.callbacks.onDisconnect?.();
  }

  /**
   * Setup WebSocket event handlers
   */
  private setupEventHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      this.log('WebSocket connected');
      this.state = WebSocketState.CONNECTED;
      this.reconnectAttempts = 0;
      this.startPingTimer();
      this.callbacks.onConnect?.();
    };

    this.ws.onclose = (event) => {
      this.log('WebSocket disconnected', { code: event.code, reason: event.reason });
      this.state = WebSocketState.DISCONNECTED;

      if (this.pingTimer) {
        clearTimeout(this.pingTimer);
        this.pingTimer = null;
      }

      // Attempt reconnection if not manually disconnected
      if (event.code !== 1000 && this.reconnectAttempts < this.config.maxReconnectAttempts!) {
        this.scheduleReconnect();
      } else {
        this.callbacks.onDisconnect?.();
      }
    };

    this.ws.onerror = (event) => {
      this.log('WebSocket error', event);
      this.handleError('WebSocket connection error');
    };

    this.ws.onmessage = (event) => {
      this.handleMessage(event.data);
    };
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(data: string): void {
    try {
      const message: WebSocketMessage = JSON.parse(data);
      this.lastMessage = message;

      this.log('Received message', { type: message.type, tenant: message.tenant_id });

      // Call general message callback
      this.callbacks.onMessage?.(message);

      // Route to specific callbacks based on message type
      switch (message.type) {
        case WebSocketMessageType.VALUE_CREATED:
          this.callbacks.onValueCreated?.(message.data);
          break;
        case WebSocketMessageType.VALUE_UPDATED:
          this.callbacks.onValueUpdated?.(message.data);
          break;
        case WebSocketMessageType.VALUE_DELETED:
          this.callbacks.onValueDeleted?.(message.data);
          break;
        case WebSocketMessageType.IMPORT_PROGRESS:
          this.callbacks.onImportProgress?.(message.data);
          break;
        case WebSocketMessageType.CONTEXT_CHANGED:
          this.callbacks.onContextChanged?.(message.data);
          break;
        default:
          this.log('Unknown message type', message.type);
      }
    } catch (error) {
      this.log('Failed to parse WebSocket message', { data, error });
    }
  }

  /**
   * Send message to WebSocket server
   *
   * @param message - Message to send
   */
  sendMessage(message: any): void {
    if (this.state !== WebSocketState.CONNECTED || !this.ws) {
      this.log('Cannot send message - not connected', message);
      return;
    }

    try {
      this.ws.send(JSON.stringify(message));
      this.log('Sent message', message);
    } catch (error) {
      this.log('Failed to send message', { message, error });
    }
  }

  /**
   * Send ping to keep connection alive
   */
  private sendPing(): void {
    this.sendMessage({
      type: 'ping',
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Start ping timer for connection keepalive
   */
  private startPingTimer(): void {
    if (this.pingTimer) {
      clearTimeout(this.pingTimer);
    }

    this.pingTimer = setTimeout(() => {
      this.sendPing();
      this.startPingTimer();
    }, this.config.pingInterval);
  }

  /**
   * Handle WebSocket errors
   */
  private handleError(message: string): void {
    this.state = WebSocketState.ERROR;
    this.log('WebSocket error', message);
    this.callbacks.onError?.(message);
  }

  /**
   * Schedule reconnection attempt
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.config.maxReconnectAttempts!) {
      this.log('Max reconnect attempts reached');
      this.handleError('Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    this.state = WebSocketState.RECONNECTING;

    const delay = this.config.reconnectInterval! * this.reconnectAttempts;
    this.log(`Scheduling reconnect attempt ${this.reconnectAttempts} in ${delay}ms`);

    this.reconnectTimer = setTimeout(() => {
      if (this.currentTenant && this.token) {
        this.connect(this.currentTenant, this.token);
      }
    }, delay);
  }

  /**
   * Subscribe to updates for a specific field definition
   *
   * @param fielddef_id - Field definition ID to subscribe to
   */
  subscribeToFieldDefinition(fielddef_id: string): void {
    this.sendMessage({
      type: 'subscribe',
      fielddef_id: fielddef_id,
    });
  }

  /**
   * Unsubscribe from updates for a specific field definition
   *
   * @param fielddef_id - Field definition ID to unsubscribe from
   */
  unsubscribeFromFieldDefinition(fielddef_id: string): void {
    this.sendMessage({
      type: 'unsubscribe',
      fielddef_id: fielddef_id,
    });
  }

  /**
   * Request current connection status
   */
  requestStatus(): void {
    this.sendMessage({
      type: 'get_status',
    });
  }

  /**
   * Get current connection state
   */
  getState(): WebSocketState {
    return this.state;
  }

  /**
   * Get current tenant context
   */
  getCurrentTenant(): string | null {
    return this.currentTenant;
  }

  /**
   * Get last received message
   */
  getLastMessage(): WebSocketMessage | null {
    return this.lastMessage;
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.state === WebSocketState.CONNECTED && this.ws?.readyState === WebSocket.OPEN;
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
export default websocketService;
